import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
  isTooSmall,
  UPLOAD_FAILED,
  UPLOAD_TOO_LARGE,
  UPLOAD_TOO_SMALL,
  UPLOAD_TYPES,
} from "@/containers/Coach/PromptStudio/hooks/useBackgroundMutations";
import { EnvContext } from "@/contexts/appContexts";
import { isApiError } from "@/services/bedrock/apiError";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { BackgroundAsset, UploadBackgroundRequest } from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";
import { getDownloadURLFirebase, saveElementToFirebaseCancellable } from "@/utils/firebase";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface LibraryUploadOptions {
  /** The Firebase folder the photos go into. */
  node: string;
  /** The library request for one uploaded photo's download URL. */
  request(url: string): UploadBackgroundRequest;
  onUploaded?(asset: BackgroundAsset): void;
}

export interface LibraryUploadProgress {
  done: number;
  total: number;
}

const uploadProgressMessage = (done: number, total: number) => `Uploading ${done} of ${total}…`;
const uploadedMessage = (n: number) => (n === 1 ? "Added to your library" : `Added ${n} backgrounds to your library`);
const uploadFailedSomeMessage = (n: number) => `${n} couldn't be uploaded. Try again.`;

const isAccepted = (file: File) => UPLOAD_TYPES.includes(file.type) && file.size <= MAX_UPLOAD_BYTES;

/**
 * Uploads photos into a background library one at a time: Firebase first, then the library record.
 * One photo reports only errors (the asset appearing is the success); several get a running count
 * and one summary.
 */
export function useLibraryUpload(options: LibraryUploadOptions) {
  const queryClient = useQueryClient();
  const envValues = React.useContext(EnvContext);
  const [progress, setProgress] = React.useState<LibraryUploadProgress | null>(null);
  // The request shape can change with the page's scope between renders; always use the newest.
  const latest = React.useRef(options);
  latest.current = options;

  const uploadOne = React.useCallback(
    async (file: File): Promise<BackgroundAsset | string> => {
      const { node, request } = latest.current;
      let url: string;
      try {
        // The Firebase helper appends the extension from the file type.
        const filename = uuidv4();
        await saveElementToFirebaseCancellable(envValues, node, file, filename);
        url = await getDownloadURLFirebase(envValues, node, file, filename);
      } catch {
        return UPLOAD_FAILED;
      }
      try {
        return await BackgroundAssetServices.upload(request(url));
      } catch (error) {
        return isApiError(error, "validation_error") && isTooSmall(error) ? UPLOAD_TOO_SMALL : UPLOAD_FAILED;
      }
    },
    [envValues]
  );

  const upload = React.useCallback(
    async (files: File[]) => {
      const accepted = files.filter(isAccepted);
      if (accepted.length < files.length) toast.error(UPLOAD_TOO_LARGE);
      if (accepted.length === 0) return;

      const batch = files.length > 1;
      const total = accepted.length;
      setProgress({ done: 0, total });
      const toastId = batch ? toast.loading(uploadProgressMessage(0, total)) : undefined;
      let uploaded = 0;
      const failures: string[] = [];

      for (const file of accepted) {
        const result = await uploadOne(file);
        if (typeof result === "string") {
          failures.push(result);
          if (!batch) toast.error(result);
        } else {
          uploaded += 1;
          latest.current.onUploaded?.(result);
        }
        const done = uploaded + failures.length;
        setProgress({ done, total });
        if (batch && done < total) toast.loading(uploadProgressMessage(done, total), { id: toastId });
      }

      if (batch) {
        // A too-small photo is the one failure a coach can fix, so it's the reason given.
        const description = failures.includes(UPLOAD_TOO_SMALL) ? UPLOAD_TOO_SMALL : undefined;
        if (uploaded > 0) {
          toast.success(uploadedMessage(uploaded), { id: toastId });
          if (failures.length > 0) toast.error(uploadFailedSomeMessage(failures.length), { description });
        } else {
          // Nothing worked: the progress toast becomes the error rather than a success.
          toast.error(uploadFailedSomeMessage(failures.length), { id: toastId, description });
        }
      }
      if (uploaded > 0) queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.all });
      setProgress(null);
    },
    [queryClient, uploadOne]
  );

  return { upload, uploading: progress !== null, progress };
}
